import React, { useState } from 'react';
import Masonry from 'react-masonry-css';
import { motion, AnimatePresence } from 'framer-motion';

const MasonryGallery = ({ images, columns = { default: 3, 1024: 2, 640: 1 } }) => {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') goToNext(e);
    if (e.key === 'ArrowLeft') goToPrevious(e);
  };

  React.useEffect(() => {
    if (lightboxOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [lightboxOpen, currentImageIndex]);
  
  return (
    <>
      <Masonry
        breakpointCols={columns}
        className="masonry-grid"
        columnClassName="masonry-grid_column"
        data-testid="masonry-gallery"
      >
        {images.map((image, index) => {
          const imageUrl = image.url || image.image_url || '';
          const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${API_BASE}${imageUrl}`;
          
          return (
            <motion.div
              key={image.id || index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="overflow-hidden rounded-sm"
              data-testid={`gallery-item-${index}`}
            >
              <div 
                className="relative group cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={fullImageUrl}
                  alt={image.alt || image.alt_text || `Gallery image ${index + 1}`}
                  className="w-full h-auto object-cover image-hover"
                  loading="lazy"
                  onError={(e) => {
                    console.error('Image load error:', fullImageUrl);
                    e.target.style.display = 'none';
                  }}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <svg 
                    className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                  </svg>
                </div>
              </div>
            </motion.div>
          );
        })}
      </Masonry>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
              onClick={closeLightbox}
              aria-label="Close"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Previous button */}
            {images.length > 1 && (
              <button
                className="absolute left-4 text-white hover:text-gray-300 transition-colors z-10"
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Image */}
            <motion.div
              key={currentImageIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="max-w-7xl max-h-[90vh] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[currentImageIndex]?.url?.startsWith('http') 
                  ? images[currentImageIndex]?.url 
                  : `${API_BASE}${images[currentImageIndex]?.url || images[currentImageIndex]?.image_url}`}
                alt={images[currentImageIndex]?.alt || images[currentImageIndex]?.alt_text || 'Gallery image'}
                className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-sm"
              />
              {/* Image counter */}
              <div className="text-white text-center mt-4 text-sm">
                {currentImageIndex + 1} / {images.length}
              </div>
            </motion.div>

            {/* Next button */}
            {images.length > 1 && (
              <button
                className="absolute right-4 text-white hover:text-gray-300 transition-colors z-10"
                onClick={goToNext}
                aria-label="Next image"
              >
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MasonryGallery;
